import React, { FC, useState, useContext, useEffect } from 'react';
import { ALL_LINES, ALL_MODELS, ALL_SERIES } from '../../../../../../constants/seriesFilter.constants';
import {
  TProductSelectorDropdowns,
  TProductSelectorLinesDropdown,
  TProductSelectorModelsDropdown,
  TProductSelectorSeriesDropdown,
} from '../../../../types/productSelector.types';
import { MultiDropdownSendData } from '../../dropdownContext';
import ProductSelectorDropdown from './dropdown';
import { windowService } from '../../../../../../utils/window.utils';

const ProductSelectorDropdowns: FC<TProductSelectorDropdowns> = ({
  id,
  fields,
  optionsList = [],
  isValidationErrors,
  defaultSelectedLine,
  defaultSelectedSeries,
  defaultSelectedModel,
}) => {
  const [lineIndex, setLineIndex] = useState<number | typeof ALL_LINES>(ALL_LINES);
  const [seriesIndex, setSeriesIndex] = useState<number | typeof ALL_SERIES>(ALL_SERIES);
  const [modelsIndex, setModelsIndex] = useState<number | typeof ALL_MODELS>(ALL_MODELS);
  const [preSelectedSeries, setPreSelectedSeries] = useState<string>('');
  const [preSelectedModel, setPreSelectedModel] = useState<string>('');

  const { updateSendData, isLineHidden, isSeriesHidden } = useContext(MultiDropdownSendData);

  const setProductIndex = (selectedLine, selectedSeries, selectedModel) => {
    const findIndex = (array, callBackFn, fallbackValue) => {
      if (!array?.length) return fallbackValue;
      const index = array.findIndex(callBackFn);
      return index !== -1 ? index : fallbackValue;
    };
    const newLineSelectedInd: typeof lineIndex = findIndex(optionsList, ({ titleUrl }) => titleUrl === selectedLine, ALL_LINES);
    const newSeriesSelectedInd = findIndex(
      optionsList[newLineSelectedInd]?.series,
      ({ titleUrl }) => titleUrl === selectedSeries,
      ALL_SERIES
    );
    const newModelSelectedInd = findIndex(
      optionsList[newLineSelectedInd]?.series?.[newSeriesSelectedInd]?.models,
      ({ titleUrl }) => titleUrl === selectedModel,
      ALL_MODELS
    );
    setLineIndex(newLineSelectedInd);
    setSeriesIndex(newSeriesSelectedInd);
    setModelsIndex(newModelSelectedInd);
    updateSendData(id, {
      line: optionsList?.[newLineSelectedInd]?.titleUrl || '',
      series: optionsList?.[newLineSelectedInd]?.series?.[newSeriesSelectedInd]?.titleUrl || '',
      models: optionsList?.[newLineSelectedInd]?.series?.[newSeriesSelectedInd]?.models?.[newModelSelectedInd]?.titleUrl || '',
      lineTitle: optionsList?.[newLineSelectedInd]?.title,
      seriesTitle: optionsList?.[newLineSelectedInd]?.series?.[newSeriesSelectedInd]?.title || '',
      modelsTitle: optionsList?.[newLineSelectedInd]?.series?.[newSeriesSelectedInd]?.models?.[newModelSelectedInd]?.title || '',
      modelsId: optionsList?.[newLineSelectedInd]?.series?.[newSeriesSelectedInd]?.models?.[newModelSelectedInd]?.id || '',
    });
  };

  useEffect(() => {
    if (optionsList.length > 0 && defaultSelectedLine) {
      setProductIndex(defaultSelectedLine, defaultSelectedSeries, defaultSelectedModel);
    }
  }, [defaultSelectedLine, defaultSelectedSeries, defaultSelectedModel, optionsList]);

  useEffect(() => {
    if (!fields.value && !isValidationErrors) {
      if (optionsList.length > 0 && defaultSelectedLine) {
        setProductIndex(defaultSelectedLine, defaultSelectedSeries, defaultSelectedModel);
      } else {
        setLineIndex(ALL_LINES);
        setSeriesIndex(ALL_SERIES);
        setModelsIndex(ALL_MODELS);
        setPreSelectedSeries('');
        setPreSelectedModel('');
      }
    }
  }, [fields.value, defaultSelectedLine, defaultSelectedSeries, defaultSelectedModel, optionsList]);

  useEffect(() => {
    if (fields.value && optionsList.length > 0) {
      const decodeValue = windowService()?.atob(fields.value)?.split(',');
      const findDropdownIndex = decodeValue?.findIndex((item) => item.split('|')?.[0] == id);
      if (findDropdownIndex > -1) {
        const [, selectedLine, selectedSeries, selectedModel] = decodeValue[findDropdownIndex]?.split('|') || [];
        setProductIndex(selectedLine, selectedSeries, selectedModel);
      }
    }
  }, [optionsList]);

  const lineOptions: TProductSelectorLinesDropdown[] = [{ id: ALL_LINES, title: '' }, ...optionsList];

  const seriesOptions: TProductSelectorSeriesDropdown[] =
    lineIndex === ALL_LINES
      ? []
      : [
        {
          id: ALL_SERIES,
          title: '',
        },
        ...(optionsList?.[lineIndex]?.series || []),
      ];

  const modelOptions: TProductSelectorModelsDropdown[] =
    seriesIndex === ALL_SERIES
      ? []
      : [
        {
          id: ALL_MODELS,
          title: '',
        },
        ...(optionsList?.[lineIndex]?.series?.[seriesIndex]?.models || []),
      ];

  const updatePreselectedOptionOnLineClick = (newLineSelectedInd, selectedLine, seriesOptions, modelOptions) => {
    const lineTitleUrl = optionsList[newLineSelectedInd == 0 ? ALL_LINES : newLineSelectedInd - 1]?.titleUrl || '';

    if (selectedLine?.titleUrl === seriesOptions?.titleUrl && selectedLine?.titleUrl === modelOptions?.titleUrl) {
      setPreSelectedSeries(seriesOptions?.title);
      setPreSelectedModel(modelOptions?.title);
      setSeriesIndex(0);
      setModelsIndex(0);
      updateSendData(id, {
        line: lineTitleUrl,
        series: seriesOptions?.titleUrl,
        models: modelOptions?.titleUrl,
        lineTitle: optionsList[newLineSelectedInd === 0 ? ALL_LINES : newLineSelectedInd - 1]?.title || '',
        seriesTitle:'',
        modelsTitle: '',
        modelsId: '',
      });
    } else if (selectedLine?.titleUrl === seriesOptions?.titleUrl) {
      setPreSelectedSeries(seriesOptions?.title);
      setPreSelectedModel('');
      setSeriesIndex(0);
      setModelsIndex(ALL_MODELS);
      updateSendData(id, {
        line: lineTitleUrl,
        series: seriesOptions?.titleUrl,
        models: '',
        lineTitle: optionsList[newLineSelectedInd === 0 ? ALL_LINES : newLineSelectedInd - 1]?.title || '',
        seriesTitle: seriesOptions?.title || '',
        modelsTitle: '',
        modelsId: '',
      });
    } else {
      setPreSelectedSeries('');
      setPreSelectedModel('');
      setSeriesIndex(ALL_SERIES);
      setModelsIndex(ALL_MODELS);
      updateSendData(id, {
        line: lineTitleUrl,
        series: '',
        models: '',
        lineTitle: optionsList[newLineSelectedInd === 0 ? ALL_LINES : newLineSelectedInd - 1]?.title || '',
        seriesTitle:'',
        modelsTitle: '',
        modelsId: '',
      });
    }
  };

  const handleLineOptionClick = (newLineSelectedInd) => {
    setLineIndex(newLineSelectedInd == 0 ? ALL_LINES : newLineSelectedInd - 1);

    const selectedLine = optionsList?.[newLineSelectedInd == 0 ? ALL_LINES : newLineSelectedInd - 1];
    const seriesOptions = selectedLine?.series?.length === 1 && selectedLine?.series?.[0];
    const modelOptions = seriesOptions?.models?.length === 1 && seriesOptions?.models?.[0];

    updatePreselectedOptionOnLineClick(newLineSelectedInd, selectedLine, seriesOptions, modelOptions);
  };

  const handleSeriesOptionClick = (newSeriesSelectedInd) => {
    setSeriesIndex(newSeriesSelectedInd === 0 ? ALL_SERIES : newSeriesSelectedInd - 1);

    const SelectedSeries = optionsList?.[lineIndex]?.series?.[newSeriesSelectedInd === 0 ? ALL_SERIES : newSeriesSelectedInd - 1];
    const modelOptions = SelectedSeries?.models?.length === 1 && SelectedSeries?.models?.[0];

    if (SelectedSeries?.titleUrl === modelOptions?.titleUrl) {
      setPreSelectedModel(modelOptions?.title);
      setModelsIndex(0);
      updateSendData(id, {
        series: optionsList?.[lineIndex]?.series?.[newSeriesSelectedInd === 0 ? ALL_SERIES : newSeriesSelectedInd - 1]?.titleUrl,
        models: modelOptions?.titleUrl,
        seriesTitle: optionsList?.[lineIndex]?.series?.[newSeriesSelectedInd === 0 ? ALL_SERIES : newSeriesSelectedInd - 1]?.title,
        modelsTitle: '',
        modelsId: '',
      });
    } else {
      setPreSelectedModel('');
      setModelsIndex(ALL_MODELS);
      updateSendData(id, {
        series: optionsList?.[lineIndex]?.series?.[newSeriesSelectedInd === 0 ? ALL_SERIES : newSeriesSelectedInd - 1]?.titleUrl,
        models: '',
        seriesTitle: optionsList?.[lineIndex]?.series?.[newSeriesSelectedInd === 0 ? ALL_SERIES : newSeriesSelectedInd - 1]?.title,
      });
    }
  };

  const handleModelOptionClick = (newModelSelectedInd) => {
    setModelsIndex(newModelSelectedInd === 0 ? ALL_MODELS : newModelSelectedInd - 1);
    updateSendData(id, {
      models:
        optionsList?.[lineIndex]?.series?.[seriesIndex]?.models?.[newModelSelectedInd === 0 ? ALL_MODELS : newModelSelectedInd - 1]
          ?.titleUrl,
      modelsTitle:
        optionsList?.[lineIndex]?.series?.[seriesIndex]?.models?.[newModelSelectedInd === 0 ? ALL_MODELS : newModelSelectedInd - 1]?.title,
      modelsId:
        optionsList?.[lineIndex]?.series?.[seriesIndex]?.models?.[newModelSelectedInd === 0 ? ALL_MODELS : newModelSelectedInd - 1]?.id,
    });
  };

  return (
    <>
      {!isLineHidden && (
        <ProductSelectorDropdown
          dropdownOptions={lineOptions}
          id={`product-selectors-line-dropdown${id}`}
          handleOptionClick={handleLineOptionClick}
          selectedOptionInd={lineIndex === ALL_LINES ? 0 : lineIndex + 1}
          fields={fields}
          label={'cnhi-forms-fields-product-selector-lines'}
          isEnabled={!defaultSelectedLine}
          dropdownID={id}
          isValidationErrors={isValidationErrors}
        />
      )}
      {!isSeriesHidden && (
        <ProductSelectorDropdown
          dropdownOptions={seriesOptions}
          id={`product-selectors-series-dropdown${id}`}
          handleOptionClick={handleSeriesOptionClick}
          selectedOptionInd={seriesIndex === ALL_SERIES ? 0 : seriesIndex + 1}
          fields={fields}
          label={'cnhi-forms-fields-product-selector-series'}
          isEnabled={!defaultSelectedSeries && !!String(seriesIndex) && lineIndex !== ALL_LINES}
          dropdownID={id}
          preSelectedOption={preSelectedSeries}
          isValidationErrors={isValidationErrors}
        />
      )}

      <ProductSelectorDropdown
        dropdownOptions={modelOptions}
        id={`product-selectors-model-dropdown${id}`}
        handleOptionClick={handleModelOptionClick}
        selectedOptionInd={modelsIndex === ALL_MODELS ? 0 : modelsIndex + 1}
        fields={fields}
        label={'cnhi-forms-fields-product-selector-models'}
        isEnabled={!defaultSelectedModel && !!String(modelsIndex) && lineIndex !== ALL_LINES && seriesIndex !== ALL_SERIES}
        dropdownID={id}
        preSelectedOption={preSelectedModel}
        isValidationErrors={isValidationErrors}
      />
    </>
  );
};

export default ProductSelectorDropdowns;
